import {
  isCalendarValid,
  NylasCalendarDontShowAgain,
  useGetNylasConnectionStatusQuery,
  useNylasCalendarDontShowAgain,
  useSelectedMethodHasMissingFields,
} from '@/features/nylas-calendar-manage-connection-modal';
import { GlobalDialogType } from '@/stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from '@/stores/global-dialogs/hooks/useGlobalDialog';
import { useContext } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { ActivateRobinJrDialog } from './ActivateRobinJrDialog/ActivateRobinJrDialog';
import { ActivateRobinJrDialogType } from './ActivateRobinJrDialog/ActivateRobinJrDialog.types';
import { GeneratedMessageDialog } from './GeneratedMessageDialog/GeneratedMessageDialog';
import { LoadingRjCommDialog } from './LoadingRjCommDialog/LoadingRjCommDialog';
import { QuestionnairePreviewDialog } from './QuestionnairePreviewDialog/QuestionnairePreviewDialog';
import { ModalState, RJCommunicationHubProps } from './RJCommunicationHub.types';
import { useContainer } from './RJCommunicationHub.useContainer';
import { WhatsAppRjCommDialog } from './WhatsAppRjCommDialog/WhatsAppRjCommDialog';

export const RJCommunicationHub = ({ candidate, initialVacancy }: RJCommunicationHubProps) => {
  const nylasCalendarManageConnectionModal = useGlobalDialog(GlobalDialogType.NylasCalendarManageConnectionModal);
  const {
    refs,
    isLoading,
    setSelectedAts,
    selectedVacancy,
    handleDialogClose,
    showLoadingProcessingVacancy,
    isGeneratingMessage,
    handleConfirmMessage,
    handleConfirmWhatsappMessage,
    handleConfirmQuestionnaire,
    modalState,
    typeActivateRobinJrDialog,
    handleConfirmSelectedVacancy,
    dontShowMessagePreview,
    setDontShowMessagePreview,
    dontShowWhatsAppMessagePreview,
    setDontShowWhatsAppMessagePreview,
    dontShowQuestionnairePreview,
    setDontShowQuestionnairePreview,
    showMessagePreviewCheckboxInActivate,
    showWhatsAppPreviewCheckboxInActivate,
    showQuestionnairePreviewCheckboxInActivate,
    improvedVacancy,
    modalNavigation,
    goBack,
    generateMessage,
    selectedIndeedProject,
    setSelectedIndeedProject,
    isWaCommunicationAgentEnabled,
  } = useContainer({ candidate, initialVacancy });

  const nylasCalendarDontShowAgain = useNylasCalendarDontShowAgain();
  const hasMissingRequiredFields = useSelectedMethodHasMissingFields();
  const { data: nylasConnectionStatus } = useGetNylasConnectionStatusQuery();
  const hasCalendarConnection = isCalendarValid(nylasConnectionStatus);

  const handleBeforeConfirmSelectedVacancy = () => {
    const isReactivate = typeActivateRobinJrDialog === ActivateRobinJrDialogType.Reactivate;
    const shouldShowSchedulerTypeModal =
      hasCalendarConnection && !isReactivate && (!nylasCalendarDontShowAgain || hasMissingRequiredFields);

    if (shouldShowSchedulerTypeModal) {
      nylasCalendarManageConnectionModal.open({
        payload: {
          skipManageConnection: true,
          footer: <NylasCalendarDontShowAgain />,
          onCancel: () => {
            nylasCalendarManageConnectionModal.close();
          },
          onFinish: () => {
            nylasCalendarManageConnectionModal.close();
            handleConfirmSelectedVacancy();
          },
        },
      });

      return;
    }

    handleConfirmSelectedVacancy();
  };

  const { dictionary, language } = useContext(LocalizationContext);

  if (isGeneratingMessage) {
    return <LoadingRjCommDialog isVisible={true} title={dictionary.writingCustomizedMessage} />;
  }

  if (showLoadingProcessingVacancy) {
    return <LoadingRjCommDialog isVisible={true} />;
  }

  if (
    isLoading &&
    (modalState === ModalState.ACTIVATE_ROBIN_JR || modalState === ModalState.LOADING_GENERATED_MESSAGE)
  ) {
    return <LoadingRjCommDialog isVisible={true} title={dictionary.writingCustomizedMessage} />;
  }

  // Show generated message dialog
  if (modalState === ModalState.GENERATED_MESSAGE) {
    return (
      <GeneratedMessageDialog
        refs={refs}
        candidate={candidate}
        selectedVacancy={selectedVacancy}
        setSelectedVacancy={setSelectedAts}
        handleConfirmMessage={handleConfirmMessage}
        handleOutreachModalRequestClose={handleDialogClose}
        dontShowMessagePreview={dontShowMessagePreview}
        setDontShowMessagePreview={setDontShowMessagePreview}
        isLoading={isLoading}
        navigateToStep={modalNavigation.navigateToStep}
        onGoBack={goBack}
        hideSelectedVacancyPill={!!initialVacancy}
        generateMessage={generateMessage}
      />
    );
  }

  if (modalState === ModalState.QUESTIONNAIRE_PREVIEW) {
    return (
      <QuestionnairePreviewDialog
        isVisible={true}
        onClose={handleDialogClose}
        onConfirm={handleConfirmQuestionnaire}
        onGoBack={goBack}
        isLoading={isLoading}
        dontShowQuestionnairePreview={dontShowQuestionnairePreview}
        setDontShowQuestionnairePreview={setDontShowQuestionnairePreview}
        candidateEsPersonId={candidate.es_person_id}
        jobId={selectedVacancy?.db_id ? Number(selectedVacancy.db_id) : undefined}
        selectedLanguage={language}
      />
    );
  }

  if (modalState === ModalState.WHATSAPP) {
    return (
      <WhatsAppRjCommDialog
        isVisible={true}
        onClose={handleDialogClose}
        onGoBack={goBack}
        onConfirm={handleConfirmWhatsappMessage}
        isLoading={isLoading}
        dontShowWhatsAppMessagePreview={dontShowWhatsAppMessagePreview}
        setDontShowWhatsAppMessagePreview={setDontShowWhatsAppMessagePreview}
        showDontShowWhatsappMessagePreviewCheckbox={isWaCommunicationAgentEnabled}
        improvedVacancy={improvedVacancy}
      />
    );
  }

  if (modalState === ModalState.LOADING) {
    return <LoadingRjCommDialog isVisible={true} title={dictionary.startingRobinJr} />;
  }

  return (
    <ActivateRobinJrDialog
      isLoading={isLoading}
      selectedAts={selectedVacancy}
      setSelectedAts={setSelectedAts}
      type={typeActivateRobinJrDialog}
      isProcessingVacancy={showLoadingProcessingVacancy}
      handleOutreachModalRequestClose={handleDialogClose}
      onConfirmSelectedVacancy={handleBeforeConfirmSelectedVacancy}
      dontShowMessagePreview={dontShowMessagePreview}
      setDontShowMessagePreview={setDontShowMessagePreview}
      showMessagePreviewCheckbox={showMessagePreviewCheckboxInActivate}
      dontShowWhatsAppMessagePreview={dontShowWhatsAppMessagePreview}
      setDontShowWhatsAppMessagePreview={setDontShowWhatsAppMessagePreview}
      showWhatsAppPreviewCheckbox={showWhatsAppPreviewCheckboxInActivate}
      dontShowQuestionnairePreview={dontShowQuestionnairePreview}
      setDontShowQuestionnairePreview={setDontShowQuestionnairePreview}
      showQuestionnairePreviewCheckbox={showQuestionnairePreviewCheckboxInActivate}
      showNylasCalendarConnectionCheckbox={nylasCalendarDontShowAgain && hasCalendarConnection}
      selectedIndeedProject={selectedIndeedProject}
      setSelectedIndeedProject={setSelectedIndeedProject}
    />
  );
};
